import React, {useState} from "react";
import {useAsync} from "react-async-hook";
import {useLocation, useParams} from "react-router-dom";
import {menuSetApi} from "../../apis/admin/menu/menuSetApi";
import {MenuSetForm, MenuSetFormValues} from "../../components/pages/admin/MenuSet/MenuSetForm";
import {useSystemMessage} from "../../hooks/useSystemMessage";
import useAppRouter from "../../hooks/useAppRouter";
import {ApiClientError, ResponseCode} from "../../apis/errors";
import {logError} from "../../libs/utils/logError";

type UrlParams = {
  id: string;
}

export const MenuSetEditPage: React.FC = () => {
  const {success} = useSystemMessage();
  const [abortController, setAbortController] = React.useState<AbortController | undefined>();
  const { id } = useParams<UrlParams>();
  const menuSetId: number = parseInt(id, 10);
  const [defaultValues, setDefaultValues] = useState<MenuSetFormValues>({
    name: '',
    content: '',
    labelSetId: null,
    labelSetName: null,
    menuList: [],
  });
  const [isLoadedDefaultValues, setIdLoadedDefaultValues] = useState<boolean>(false);
  const [ apiClientError, setApiClientError] = useState<ApiClientError | null>(null);
  const { goto } = useAppRouter();
  const location = useLocation();

  useAsync(async () => {
    const fetch = async () => {
      setAbortController(new AbortController());
      try
      {
        const response = await menuSetApi.show(menuSetId, { abortSignal: abortController?.signal });
        setDefaultValues({
          name: response.menuSet.name,
          content: response.menuSet.content,
          labelSetId: response.menuSet.labelSetId,
          labelSetName: response.menuSet.labelSetName,
          menuList: response.menuSet.menuList,
        });
        setAbortController(undefined);
        setIdLoadedDefaultValues(true);

      } catch(e) {
        await logError(
          location.pathname,
          'メニューセット詳細の取得に失敗しました。',
          e
        );

        if ((e instanceof ApiClientError) && (e.code === ResponseCode.NOT_FOUND)) {
          goto('notFoundError');
          return;
        }

        goto('internalServerError');
      }
    };
    fetch();
  }, []);

  const clearApiClientError = async (): Promise<void> => {
    setApiClientError(null);
  }

  const onSave = async (data: MenuSetFormValues): Promise<void> => {
    setAbortController(new AbortController());
    try {
      await menuSetApi.update(
        menuSetId
        ,{
          name: data.name,
          content: data.content,
          labelSetId: data.labelSetId,
          menuList: data.menuList,
        },
        { abortSignal: abortController?.signal });
      setAbortController(undefined);

      await success('メニューセットを更新しました。');
      goto('menuSetList')

    } catch (e) {
      await logError(
        location.pathname,
        'メニューセットの更新に失敗しました。',
        e
      );

      if (e instanceof ApiClientError) {
        setApiClientError(e);
        return;
      }

      goto('internalServerError');
    }
  }

  return (
    <>
      {isLoadedDefaultValues &&
        <MenuSetForm
          {...{
            defaultValues,
            isEdit: true,
            apiClientError,
            clearApiClientError,
            onSave,
          }}
        />
      }
    </>
  );
}
